import React, { useState } from 'react'
import { Stack, useTheme, Typography } from '@mui/material'
import Switch from 'packages/ui/Switch'
import { useTranslation } from 'react-i18next'

import { toast } from 'components/global/CUK/Toast/Toast'

import useSilentMode from '../use-silent-mode'

interface ITitleProps {
  title: string
  checked: boolean
  disabled: boolean
  onToggle?: () => void
}

interface ISite {
  id: number
  name: string
  checked: boolean
}
interface IGroup {
  code: string
  name: string
  email: string
  sites: ISite[]
}

interface ICardProps {
  group: IGroup
}

interface IRowBodyProps {
  site: ISite
  disabled: boolean
  isLast: boolean
}

const SITE_WIDTH = 360
const STATUS_WIDTH = 80
const ACTION_WIDTH = 62

const Title : React.FC<ITitleProps> = ({title, disabled, checked, onToggle}) => {
  const theme = useTheme()
  return (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      sx={{
        padding: '12px 24px'
      }}
    >
      <Typography
        component={'span'}
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: theme.palette.primary.main,
        }}>{title}</Typography>
      {
        Boolean(onToggle) && (
          <Switch onClick={onToggle} checked={checked} disabled={disabled} />
        )
      }
    </Stack>
  )
}

const HeaderSite: React.FC = () => {
  const theme = useTheme()
  const {t} = useTranslation()
  return (
    <Stack
      direction={'row'}
      sx={{
        backgroundColor: theme.palette.grey[50],
        padding: '8px 24px',
        borderTop: `1px solid ${theme.palette.grey[100]}`,
        borderBottom: `1px solid ${theme.palette.grey[100]}`
      }}
    >
      <Typography sx={{width: SITE_WIDTH, fontSize: 10}} component={'span'}>{t('silent_mode.header.site')}</Typography>
      <Typography sx={{width: STATUS_WIDTH, fontSize: 10, color: theme.palette.grey[400]}} component={'span'}>{t('silent_mode.header.status')}</Typography>
      <Typography sx={{width: ACTION_WIDTH, fontSize: 10, color: theme.palette.grey[400], textAlign: 'right'}} component={'span'}>{t('silent_mode.header.action')}</Typography>
    </Stack>
  )
}

const BodySite: React.FC<IRowBodyProps> = ({
  site,
  disabled,
  isLast,
  }) => {
  const theme = useTheme()
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const {enableSite, disableSite} = useSilentMode()
  return (
    <Stack
      direction={'row'}
      sx={{
        padding: '8px 24px',
        borderBottom: isLast ? 0 : `1px solid ${theme.palette.grey[100]}`,
        backgroundColor: site.checked ? '#EFF5FF' : 'inherit',
        ...isLast && {
          borderBottomRightRadius: 16,
          borderBottomLeftRadius: 16
        }
      }}
    >
      <Typography sx={{width: SITE_WIDTH, fontSize: 14, fontWeight: 500, ...!site.checked && {color: theme.palette.grey[500]}}} component={'span'}>{site.name}</Typography>
      <Typography sx={{width: STATUS_WIDTH, fontSize: 14, ...!site.checked && {color: theme.palette.grey[400]}}} component={'span'}>
        {site.checked ? t('silent_mode.off_line') : t('silent_mode.on_line')}
      </Typography>
      <Stack sx={{width: ACTION_WIDTH}} alignItems={'flex-end'}>
        <Switch
          disabled={disabled || loading}
          checked={site.checked}
          onClick={async () => {
            setLoading(true)
            try {
              if (!site.checked) {
                await enableSite(site.id)
                toast.success(t('silent_mode.alert.site_off_line', {site: site.name}))
              } else {
                await disableSite(site.id)
                toast.success(t('silent_mode.alert.site_on_line', {site: site.name}))
              }
            } catch (e) {
              toast.error(t('actions.error'))
            }
            setLoading(false)
          }}

        />
      </Stack>
    </Stack>
  )
}

const Group: React.FC<ICardProps> = ({ group }) => {
  const theme = useTheme()
  const [loading, setLoading] = useState<boolean>(false)
  const {enableGroup, disableGroup} = useSilentMode()
  const {t} = useTranslation()
  return (
    <Stack
      sx={{
        border: `1px solid ${theme.palette.grey[100]}`,
        borderRadius: theme.spacing(2),
        width: 552
      }}>
      <Title
        title={group.name}
        checked={group.sites.every(site => site.checked)}
        disabled={loading}
        onToggle={group.sites.some(site => site.checked) ? async () => {
          setLoading(true)
          try {
            if (!group.sites.every(site => site.checked)) {
              await enableGroup(group.code)
              toast.success(t('silent_mode.alert.group_off_line', {group: group.name}))
            } else {
              await disableGroup(group.code)
              toast.success(t('silent_mode.alert.group_on_line', {group: group.name}))
            }
          } catch (e) {
            toast.error(t('actions.error'))
          }
          setLoading(false)
        } : undefined}
      />
      <HeaderSite />
      {
        group.sites.map((site, i) => {
          return (
            <BodySite
              key={site.id}
              site={site}
              disabled={loading}
              isLast={i === (group.sites.length - 1)}
            />
          )
        })
      }
    </Stack>
  )
}

export default Group